import {
  forwardRef,
  InputHTMLAttributes,
  KeyboardEventHandler,
  useCallback,
  useEffect,
  useRef,
  useState,
  FC,
} from 'react';

import { alignRightClass, disabledDecimalsClass, isDecimalClass } from './InputMeterReading.css';
import { useInputIds } from '../../hooks/useInputIds';
import { ChangeHandler } from '../../hooks/useNumberInput';
import { useValidationErrorEvent } from '../../hooks/useValidationErrorEvent';
import { useI18nTranslation } from '../../providers/i18n';
import { mergeRefs } from '../../util';
import { Box } from '../Box/Box';
import { InputBaseProps, StyledInput } from '../Input/Input';
import { InputError } from '../Input/InputError';
import { Hint } from '../Input/InputHint';
import { Stack } from '../Stack/Stack';
import { Stretch } from '../Stretch/Stretch';
import { Text } from '../Text/Text';
import { VisuallyHidden } from '../VisuallyHidden/VisuallyHidden';

function sliceMaxDecimals(dec = '', max = 2) {
  return dec ? dec.slice(0, max) : '';
}

function formatValue(integerPart: string, decimalPart: string): string {
  return decimalPart ? `${integerPart}.${decimalPart}` : integerPart;
}

const DisabledDecimal: FC = () => (
  <Box borderRadius="s" backgroundColor="backgroundTertiary" paddingX="2">
    <Stretch>
      <Stack alignY="center">
        <Text color="textLowEmphasis" size="BodyL" weight="bold">
          <span role="presentation">X</span>
        </Text>
      </Stack>
    </Stretch>
  </Box>
);

type Unit = 'Wh' | 'kWh' | 'm3' | 'GJ';
type UnitSymbol = 'Wh' | 'kWh' | 'm³' | 'GJ';
type InUnitLabel = 'inWattHour' | 'inKilowattHour' | 'inCubicMetres' | 'inGigajoule';
const unitMap: Record<
  Unit,
  {
    symbol: UnitSymbol;

    inLabel: InUnitLabel;
  }
> = {
  Wh: { symbol: 'Wh', inLabel: 'inWattHour' },
  kWh: { symbol: 'kWh', inLabel: 'inKilowattHour' },
  m3: { symbol: 'm³', inLabel: 'inCubicMetres' },
  GJ: { symbol: 'GJ', inLabel: 'inGigajoule' },
};

/**
 * Native input element attributes picked from InputHTMLAttributes. More info on what attributes
 * are available: https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#attributes
 */

type InputMeterReadingHTMLAttributes = Pick<
  InputHTMLAttributes<HTMLInputElement>,
  'placeholder' | 'value' /** used for controlled inputs */ | 'onChange' | 'onBlur' | 'onFocus'
>;

interface InputMeterReadingProps extends InputBaseProps {
  /**
   * Takes care of the label helper and unit suffix.
   */

  maximumFractionDigits?: number;
  placeholderDecimal?: string;
  unit: Unit;
}

interface Props extends InputMeterReadingProps, InputMeterReadingHTMLAttributes {
  defaultValue?: number | string;
  onChange?({ target, type }: ChangeHandler): void;
}

export const InputMeterReading = forwardRef<HTMLInputElement, Props>(
  (
    {
      unit,
      label,
      maximumFractionDigits = 3,
      isOptional,
      error,
      hint,
      isDisabled,
      isReadOnly,
      name,
      defaultValue = '',
      placeholder,
      placeholderDecimal,
      value,
      onBlur,
      onChange,
      ...rest
    },

    ref,
  ) => {
    const { inputId, describedBy, errorId, hintId } = useInputIds({ error, hint });
    const i18nOptionalText = useI18nTranslation('optional');
    const i18nInUnitText = useI18nTranslation(unitMap[unit].inLabel);
    const mockRef = useRef<HTMLInputElement>(null);
    const localRefInteger = useRef<HTMLInputElement>(null);
    const localRefDecimal = useRef<HTMLInputElement>(null);
    const mergedRef = mergeRefs([ref, mockRef]);

    const [integerPart, setIntegerPart] = useState<string>(defaultValue?.toString() ?? '');
    const [decimalPart, setDecimalPart] = useState<string>('');

    useValidationErrorEvent({ error, name }, mockRef);

    const onKeyDown: KeyboardEventHandler<HTMLInputElement> = event => {
      const target = event.target as HTMLInputElement;
      const caretPos = target.selectionStart;
      const totalLength = target.value.length;

      if ([',', '.'].includes(event.key)) {
        event.preventDefault();

        if (localRefDecimal.current && caretPos === totalLength) {
          localRefDecimal.current.focus();
          localRefDecimal.current.select();
          return;
        }
      }
    };

    const handleOnChange = useCallback(
      (value: string) => {
        const [int = '', dec = ''] = value.toString().split(/[.,]/, 2);
        setIntegerPart(int);
        const slicedDecimal = sliceMaxDecimals(dec, maximumFractionDigits);
        setDecimalPart(slicedDecimal);

        if (mockRef.current) {
          mockRef.current.value = formatValue(int, slicedDecimal);
          onChange?.({
            target: mockRef.current,
            type: 'change',
          });
        }
      },
      [maximumFractionDigits, onChange],
    );

    useEffect(() => {
      if (defaultValue || value) {
        const input = defaultValue || value || '';
        const [int = '', dec = ''] = input.toString().split(/[.,]/, 2);
        setIntegerPart(int);
        const slicedDecimal = sliceMaxDecimals(dec, maximumFractionDigits);
        setDecimalPart(slicedDecimal);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultValue, maximumFractionDigits]);

    return (
      <div>
        <Stack direction="row" gap="3" alignX="end" alignY="end">
          <Stack.Item grow>
            <label htmlFor={inputId}>
              <Stack gap="2" alignY="center">
                <Text size="BodyS" weight="bold">
                  {label}

                  <VisuallyHidden>{i18nInUnitText}</VisuallyHidden>

                  {isOptional ? <span>{` (${i18nOptionalText})`}</span> : null}
                </Text>

                <Stack direction="row" gap="2" alignY="end">
                  <Stack.Item grow>
                    <VisuallyHidden ariaLive="off">
                      <input
                        tabIndex={-1}
                        id={inputId}
                        aria-hidden="true"
                        ref={mergedRef}
                        name={name}
                        aria-invalid={error ? true : undefined}
                        aria-describedby={describedBy}
                        onFocus={() => {
                          if (localRefInteger) localRefInteger.current?.focus();
                        }}
                        value={formatValue(integerPart, decimalPart)}
                        readOnly
                      />
                    </VisuallyHidden>

                    <StyledInput
                      ref={localRefInteger}
                      name={`${name}-visual`}
                      type="text"
                      inputMode="numeric"
                      aria-describedby={describedBy}
                      aria-invalid={error ? true : undefined}
                      disabled={isDisabled}
                      readOnly={isReadOnly}
                      isInvalid={!!error}
                      className={alignRightClass}
                      placeholder={placeholder}
                      value={integerPart}
                      onChange={event => {
                        handleOnChange(`${event.target.value}.${decimalPart}`);
                      }}
                      onKeyDown={onKeyDown}
                      onBlur={({ target, ...blurEventProps }) => {
                        // Manually call the the passed blur with the mocked ref
                        if (mockRef.current && onBlur) {
                          onBlur({ target: mockRef?.current, ...blurEventProps });
                        }
                      }}
                      autoComplete="off"
                      {...rest}
                    />
                  </Stack.Item>

                  <Stack.Item>
                    <Text size="BodyS" weight="bold">
                      ,
                    </Text>
                  </Stack.Item>

                  {maximumFractionDigits > 0 ? (
                    <Stack.Item className={isDecimalClass}>
                      <StyledInput
                        ref={localRefDecimal}
                        name={`${name}-visual-decimal`}
                        type="text"
                        inputMode="numeric"
                        aria-describedby={describedBy}
                        aria-invalid={error ? true : undefined}
                        disabled={isDisabled}
                        readOnly={isReadOnly}
                        isInvalid={!!error}
                        maxLength={maximumFractionDigits}
                        placeholder={placeholderDecimal}
                        value={decimalPart}
                        onChange={event => {
                          handleOnChange(`${integerPart}.${event.target.value}`);
                        }}
                        autoComplete="off"
                        {...rest}
                      />
                    </Stack.Item>
                  ) : (
                    <Stack.Item className={disabledDecimalsClass}>
                      <Stretch height>
                        <Stack direction="row" gap="1" alignX="justify">
                          <DisabledDecimal />

                          <DisabledDecimal />

                          <DisabledDecimal />
                        </Stack>
                      </Stretch>
                    </Stack.Item>
                  )}
                </Stack>
              </Stack>
            </label>

            {error ? <InputError id={errorId}>{error}</InputError> : null}
          </Stack.Item>

          <Stack.Item>
            <Text size="BodyS" color="textPrimary" weight="bold">
              {unitMap[unit].symbol}
            </Text>

            {error ? <div style={{ height: '41px' }} /> : null}
          </Stack.Item>
        </Stack>

        {hint ? (
          <Box paddingTop="2">
            <Hint id={hintId}>{hint}</Hint>
          </Box>
        ) : null}
      </div>
    );
  },
);

InputMeterReading.displayName = 'InputMeterReading';
